import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Select from "react-select";
import baseURL from "../../Services/Url";
import SideBar from "../dashboard/SideBar";
import FirstNavbar from "../dashboard/FirstNavbar";
import {
  blocksFromBlog,
  cleanContentBlocks,
  legacyFieldsFromBlocks,
} from "./BlogContentBlocks";
import BlogContentBlocks from "./BlogContentBlocks";

const slugify = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const splitCsv = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

function BlogAdd() {
  const [loader, setLoader] = useState(false);
  const [tagLoader, setTagLoader] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [blogFormData, setBlogFormData] = useState({
    title: "",
    contentBlocks: blocksFromBlog(),
    report_date: "",
    blog_type: "",
    author: "",
    featuredImage: null,
    altText: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    tags: [],
  });
  const [formErrors, setFormErrors] = useState({});

  const selectedTagOptions = useMemo(
    () =>
      tagOptions.filter((option) =>
        blogFormData.tags.includes(option.value)
      ),
    [blogFormData.tags, tagOptions]
  );

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = () => {
    axios
      .get(`${baseURL}/tags`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setTagOptions(
            res.data.map((tag) => ({
              value: tag.slug || tag.name || tag.title,
              label: tag.name || tag.title || tag.slug,
            }))
          );
        }
      })
      .catch(() => {
        toast.error("Unable to load blog tags");
      });
  };

  const updateField = (name, value) => {
    setBlogFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "title" && !prev.slug ? { slug: slugify(value) } : {}),
      ...(name === "title" && !prev.metaTitle ? { metaTitle: value } : {}),
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    updateField(name, name === "featuredImage" ? files[0] : value);
  };

  const handleTagsChange = (options) => {
    updateField(
      "tags",
      (options || []).map((option) => option.value)
    );
  };

  const handleCreateTag = () => {
    const name = newTagName.trim();
    if (!name) {
      toast.error("Please enter a tag name");
      return;
    }

    setTagLoader(true);
    axios
      .post(`${baseURL}/tags`, {
        name,
        slug: slugify(name),
        metaTitle: name,
      })
      .then((res) => {
        const createdTag = res.data || {};
        const option = {
          value: createdTag.slug || slugify(name),
          label: createdTag.name || name,
        };
        setTagOptions((prev) => [...prev, option]);
        setBlogFormData((prev) => ({
          ...prev,
          tags: Array.from(new Set([...prev.tags, option.value])),
        }));
        setNewTagName("");
        toast.success("Tag created");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to create tag");
      })
      .finally(() => setTagLoader(false));
  };

  const handleValidation = () => {
    const {
      title,
      contentBlocks,
      report_date,
      blog_type,
      author,
      featuredImage,
      altText,
      slug,
      metaTitle,
      metaDescription,
    } = blogFormData;
    const newErrors = {};

    if (!title) newErrors.title = "Please enter a title";
    const cleanedBlocks = cleanContentBlocks(contentBlocks);
    if (!cleanedBlocks.some((block) => block.type === "text")) {
      newErrors.contentBlocks = "Please add at least one text content block";
    }
    if (!report_date) newErrors.report_date = "Please select a report date";
    if (!blog_type) newErrors.blog_type = "Please enter a blog type";
    if (!author) newErrors.author = "Please enter an author";
    if (!featuredImage) newErrors.featuredImage = "Please select an image";
    if (!altText) newErrors.altText = "Please enter image alt text";
    if (!slug) newErrors.slug = "Please enter a URL slug";
    if (!metaTitle) newErrors.metaTitle = "Please enter a meta title";
    if (!metaDescription) {
      newErrors.metaDescription = "Please enter a meta description";
    }

    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = handleValidation();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setLoader(true);
    const contentBlocks = cleanContentBlocks(blogFormData.contentBlocks);
    const legacy = legacyFieldsFromBlocks(contentBlocks);
    const formData = new FormData();
    formData.append("title", blogFormData.title);
    formData.append("description", legacy.content);
    formData.append("report_date", blogFormData.report_date);
    formData.append("blog_type", blogFormData.blog_type);
    formData.append("author", blogFormData.author);
    formData.append("featuredImage", blogFormData.featuredImage);
    formData.append("category", "blog");
    formData.append("slug", slugify(blogFormData.slug));
    formData.append("metaTitle", blogFormData.metaTitle);
    formData.append("metaDescription", blogFormData.metaDescription);
    formData.append("metaKeywords", JSON.stringify(splitCsv(blogFormData.metaKeywords)));
    formData.append("altText", blogFormData.altText);
    formData.append("tags", JSON.stringify(blogFormData.tags));
    formData.append("contentBlocks", JSON.stringify(contentBlocks));
    formData.append("charts", JSON.stringify(legacy.charts));
    formData.append("graphs", JSON.stringify([]));
    formData.append("tables", JSON.stringify(legacy.tables));
    formData.append("content", legacy.content);

    fetch(`${baseURL}/blogs`, {
      method: "POST",
      body: formData,
    })
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || data || "Unable to create blog");
        }

        setBlogFormData({
          title: "",
          contentBlocks: blocksFromBlog(),
          report_date: "",
          blog_type: "",
          author: "",
          featuredImage: null,
          altText: "",
          slug: "",
          metaTitle: "",
          metaDescription: "",
          metaKeywords: "",
          tags: [],
        });
        toast.success("Blog created successfully");
      })
      .catch((err) => {
        toast.error(err.message || "Unable to create blog");
      })
      .finally(() => setLoader(false));
  };

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar
        transition={Zoom}
        delay={500}
        limit={1}
      />
      <div>
        <div className="sidebar">
          <SideBar />
        </div>

        <div className="content">
          <div className="container">
            <FirstNavbar />
            <div className="row my-2">
              <div className="col-md-4"></div>
              <div className="col-md-3">
                <h3>
                  <b>Add Blog</b>
                </h3>
              </div>
              <div className="col-md-4 text-end">
                <button
                  type="button"
                  className="btn btn-outline-success mx-1"
                  disabled
                >
                  <Link to="/blog-add">Add Blog</Link>
                </button>
                <button type="button" className="btn btn-outline-success mx-1">
                  <Link to="/blog-list">Blog List</Link>
                </button>
              </div>
            </div>

            <div className="container mt-4 mb-5">
              <Form>
                <Row>
                  <Col md={8}>
                    <Form.Group controlId="formTitle" className="mb-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={blogFormData.title}
                        onChange={handleChange}
                        placeholder="Enter title"
                      />
                      <span className="text-danger">{formErrors.title}</span>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formSlug" className="mb-3">
                      <Form.Label>URL Slug</Form.Label>
                      <Form.Control
                        type="text"
                        name="slug"
                        value={blogFormData.slug}
                        onChange={(event) =>
                          updateField("slug", slugify(event.target.value))
                        }
                        placeholder="blog-url-slug"
                      />
                      <span className="text-danger">{formErrors.slug}</span>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group controlId="formReportDate" className="mb-3">
                      <Form.Label>Report Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="report_date"
                        value={blogFormData.report_date}
                        onChange={handleChange}
                      />
                      <span className="text-danger">
                        {formErrors.report_date}
                      </span>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formBlogType" className="mb-3">
                      <Form.Label>Blog Type</Form.Label>
                      <Form.Control
                        type="text"
                        name="blog_type"
                        value={blogFormData.blog_type}
                        onChange={handleChange}
                        placeholder="Enter blog type"
                      />
                      <span className="text-danger">{formErrors.blog_type}</span>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="formAuthor" className="mb-3">
                      <Form.Label>Author</Form.Label>
                      <Form.Control
                        type="text"
                        name="author"
                        value={blogFormData.author}
                        onChange={handleChange}
                        placeholder="Enter author"
                      />
                      <span className="text-danger">{formErrors.author}</span>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formImage" className="mb-3">
                      <Form.Label>Featured Image</Form.Label>
                      <Form.Control
                        type="file"
                        name="featuredImage"
                        onChange={handleChange}
                      />
                      <span className="text-danger">
                        {formErrors.featuredImage}
                      </span>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formAltText" className="mb-3">
                      <Form.Label>Featured Image Alt Tag</Form.Label>
                      <Form.Control
                        type="text"
                        name="altText"
                        value={blogFormData.altText}
                        onChange={handleChange}
                        placeholder="Describe the image for SEO and accessibility"
                      />
                      <span className="text-danger">{formErrors.altText}</span>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="border-top pt-3 mt-2">
                  <h5>SEO</h5>
                  <Form.Group controlId="formMetaTitle" className="mb-3">
                    <Form.Label>Title Tag</Form.Label>
                    <Form.Control
                      type="text"
                      name="metaTitle"
                      value={blogFormData.metaTitle}
                      onChange={handleChange}
                      placeholder="Meta title shown in search results"
                    />
                    <span className="text-danger">{formErrors.metaTitle}</span>
                  </Form.Group>

                  <Form.Group controlId="formMetaDescription" className="mb-3">
                    <Form.Label>Meta Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="metaDescription"
                      value={blogFormData.metaDescription}
                      onChange={handleChange}
                      placeholder="Short search result description"
                    />
                    <span className="text-danger">
                      {formErrors.metaDescription}
                    </span>
                  </Form.Group>

                  <Form.Group controlId="formMetaKeywords" className="mb-3">
                    <Form.Label>Meta Keywords</Form.Label>
                    <Form.Control
                      type="text"
                      name="metaKeywords"
                      value={blogFormData.metaKeywords}
                      onChange={handleChange}
                      placeholder="keyword one, keyword two"
                    />
                  </Form.Group>
                </div>

                <div className="border-top pt-3 mt-2">
                  <h5>Blog Tags</h5>
                  <Form.Group controlId="formTags" className="mb-3">
                    <Form.Label>Assign Tags</Form.Label>
                    <Select
                      isMulti
                      options={tagOptions}
                      value={selectedTagOptions}
                      onChange={handleTagsChange}
                      placeholder="Select blog tags"
                    />
                  </Form.Group>

                  <Row className="align-items-end">
                    <Col md={8}>
                      <Form.Group controlId="formCreateTag" className="mb-3">
                        <Form.Label>Create Tag</Form.Label>
                        <Form.Control
                          type="text"
                          value={newTagName}
                          onChange={(event) => setNewTagName(event.target.value)}
                          placeholder="New tag name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Button
                        variant="outline-primary"
                        type="button"
                        className="mb-3 w-100"
                        onClick={handleCreateTag}
                        disabled={tagLoader}
                      >
                        {tagLoader ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          "Create and Assign"
                        )}
                      </Button>
                    </Col>
                  </Row>
                </div>

                <BlogContentBlocks
                  blocks={blogFormData.contentBlocks}
                  onChange={(contentBlocks) => updateField("contentBlocks", contentBlocks)}
                  onError={(err) =>
                    toast.error(
                      err.response?.data?.message || err.message || "Unable to upload content image"
                    )
                  }
                />
                <span className="text-danger d-block mb-3">
                  {formErrors.contentBlocks}
                </span>

                <Button
                  variant="primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={loader}
                >
                  {loader ? <Spinner animation="border" size="sm" /> : "Submit"}
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BlogAdd;
